import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Button, Input, Modal, Spin } from 'antd';
import {
  VerticalAlignTopOutlined,
  LeftOutlined,
  RightOutlined,
  VerticalAlignBottomOutlined,
  PlusOutlined,
  EditOutlined,
  SaveOutlined,
  RollbackOutlined,
  DeleteOutlined,
  SearchOutlined,
  LogoutOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { usePWG208Mode } from './hooks/usePWG208Mode';
import { usePWG208Record } from './hooks/usePWG208Record';
import { usePWG208Navigation } from './hooks/usePWG208Navigation';
import { usePWG208Approval } from './hooks/usePWG208Approval';
import { pwg208Api } from '../../services/pwg208Api';
import { BatchAcceptanceBridge, pwg208f1AcceptanceManifest } from '../../features/acceptance';
import Tab1Main from './components/Tab1Main';
import Tab2Details from './components/Tab2Details';
import Tab3Prepayments from './components/Tab3Prepayments';
import Tab4InvoiceItems from './components/Tab4InvoiceItems';
import Tab5AuditLogs from './components/Tab5AuditLogs';
import './PWG208Form.css';

const PWG208Form: React.FC = () => {
  const navigate = useNavigate();
  const { mode, isEditing, setAdd, setEdit, setBrowse } = usePWG208Mode();
  const [activeTab, setActiveTab] = useState('1');
  const [searchText, setSearchText] = useState('');

  const {
    record, formData, details, prepayments, invoiceItems, loading,
    loadRecord, resetForAdd, revertToRecord,
    getField, setField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow, updateDetailRow,
    addPrepaymentRow, deletePrepaymentRow, updatePrepaymentRow,
    addInvoiceRow, deleteInvoiceRow, updateInvoiceRow,
  } = usePWG208Record();

  const loadWithRecord = useCallback(
    async (id: string) => {
      await loadRecord(id);
    },
    [loadRecord]
  );

  const {
    allIds, currentIdx,
    loadList, navFirst, navPrev, navNext, navLast,
    appendId, removeId,
  } = usePWG208Navigation(loadWithRecord);

  const { handleSubmitClick } = usePWG208Approval({
    record,
    isEditing,
    loadRecord,
  });

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleAdd = useCallback(() => {
    resetForAdd();
    setAdd();
    setActiveTab('1');
  }, [resetForAdd, setAdd]);

  const handleEdit = useCallback(() => {
    if (!record) return;
    setEdit();
  }, [record, setEdit]);

  const handleCancel = useCallback(() => {
    revertToRecord();
    setBrowse();
  }, [revertToRecord, setBrowse]);

  const handleSaveClick = useCallback(async () => {
    const isAdd = mode === 'add';
    await handleSave(isAdd, (saved) => {
      setBrowse();
      if (isAdd) {
        appendId(saved.fg160_01);
      }
    });
  }, [mode, handleSave, setBrowse, appendId]);

  const handleDeleteClick = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除申請單「${record.fg160_01}」？此操作無法復原。`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () =>
        handleDelete(() => {
          removeId(record.fg160_01);
          loadList();
        }),
    });
  }, [record, handleDelete, removeId, loadList]);

  const handleSearch = useCallback(() => {
    const trimmed = searchText.trim();
    if (!trimmed) return;
    const idx = allIds.indexOf(trimmed);
    if (idx >= 0) {
      navFirst();
      // navigate to found index
      const found = allIds.findIndex((id) => id === trimmed);
      if (found >= 0) loadRecord(trimmed);
    } else {
      loadRecord(trimmed).catch(() => {});
    }
  }, [searchText, allIds, navFirst, loadRecord]);

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';
  const navInfo =
    allIds.length > 0
      ? `${currentIdx + 1} / ${allIds.length}`
      : '無資料';

  const auditLogs = record?.audit_logs ?? [];
  const queryAcceptanceExact = useCallback((id: string) => pwg208Api.getByID(id), []);

  const tabItems = [
    {
      key: '1',
      label: '基本資料',
      children: (
        <Tab1Main
          formData={formData}
          isEditing={isEditing}
          getField={getField}
          setField={setField}
          onLookupConfirm={() => {}}
        />
      ),
    },
    {
      key: '2',
      label: `開票明細 (${details.length})`,
      children: (
        <Tab2Details
          details={details}
          formData={formData}
          isEditing={isEditing}
          onAdd={addDetailRow}
          onDelete={deleteDetailRow}
          onUpdate={updateDetailRow}
          getField={getField}
          setField={setField}
        />
      ),
    },
    {
      key: '3',
      label: `預收款 (${prepayments.length})`,
      children: (
        <Tab3Prepayments
          prepayments={prepayments}
          isEditing={isEditing}
          onAdd={addPrepaymentRow}
          onDelete={deletePrepaymentRow}
          onUpdate={updatePrepaymentRow}
        />
      ),
    },
    {
      key: '4',
      label: `發票號碼 (${invoiceItems.length})`,
      children: (
        <Tab4InvoiceItems
          invoiceItems={invoiceItems}
          isEditing={isEditing}
          onAdd={addInvoiceRow}
          onDelete={deleteInvoiceRow}
          onUpdate={updateInvoiceRow}
        />
      ),
    },
    {
      key: '5',
      label: `審核日誌 (${auditLogs.length})`,
      children: <Tab5AuditLogs auditLogs={auditLogs} />,
    },
  ];

  return (
    <Spin spinning={loading}>
      <div className="pwg208-form">
        <div className="page-wrapper">
          {/* Title bar */}
          <div className="page-title-bar">
            <div className="page-title-left">
              <nav className="breadcrumb-inline">
                <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
                <span>/</span>
                <span>財務管理</span>
                <span>/</span>
                <span className="current">開票申請單</span>
              </nav>
              <span className="title-separator">&mdash;</span>
              <h1>開票申請單</h1>
              <span className="badge">G208</span>
            </div>
            <div className="page-title-right">
              <span className="mode-label">[{modeLabel}]</span>
              <span className="nav-info">{navInfo}</span>
            </div>
          </div>

          {/* Search bar */}
          <div className="search-bar">
            <Input
              placeholder="輸入申請單號查詢"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 200 }}
              allowClear
            />
            <Button icon={<SearchOutlined />} onClick={handleSearch}>
              查詢
            </Button>
          </div>

          {/* Tabs content */}
          <div className="tabs-container">
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              size="small"
            />
          </div>
        </div>

        {/* Fixed action bar */}
        <div className="action-bar">
          {/* Navigation */}
          <Button
            icon={<VerticalAlignTopOutlined />}
            size="small"
            onClick={navFirst}
            disabled={isEditing || currentIdx <= 0}
            title="最前"
          />
          <Button
            icon={<LeftOutlined />}
            size="small"
            onClick={navPrev}
            disabled={isEditing || currentIdx <= 0}
            title="上一筆"
          />
          <Button
            icon={<RightOutlined />}
            size="small"
            onClick={navNext}
            disabled={isEditing || currentIdx >= allIds.length - 1}
            title="下一筆"
          />
          <Button
            icon={<VerticalAlignBottomOutlined />}
            size="small"
            onClick={navLast}
            disabled={isEditing || currentIdx >= allIds.length - 1}
            title="最後"
          />

          <span className="action-separator" />

          {/* Edit actions */}
          {!isEditing ? (
            <>
              <Button
                data-acceptance-action="PWG208-ACT-CREATE"
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={handleAdd}
                title="新增"
              >
                新增
              </Button>
              <Button
                data-acceptance-action="PWG208-ACT-UPDATE"
                icon={<EditOutlined />}
                size="small"
                onClick={handleEdit}
                disabled={!record}
                title="修改"
              >
                修改
              </Button>
              <Button
                data-acceptance-action="PWG208-ACT-DELETE"
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={handleDeleteClick}
                disabled={!record}
                title="刪除"
              >
                刪除
              </Button>
              <Button
                data-acceptance-action="PWG208-ACT-SUBMIT"
                icon={<SendOutlined />}
                size="small"
                onClick={handleSubmitClick}
                disabled={!record || record.fg160_80 !== 'A'}
                title="送審"
              >
                送審
              </Button>
            </>
          ) : (
            <>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                size="small"
                onClick={handleSaveClick}
                title="存檔"
              >
                存檔
              </Button>
              <Button
                icon={<RollbackOutlined />}
                size="small"
                onClick={handleCancel}
                title="還原"
              >
                還原
              </Button>
            </>
          )}

          <span className="action-separator" />

          <Button
            data-acceptance-action="PWG208-ACT-PRINT"
            size="small"
            disabled
            title="Contract 已揭露：列印尚未實作"
          >
            列印（尚未實作）
          </Button>

          <Button
            icon={<LogoutOutlined />}
            size="small"
            onClick={() => navigate('/erp')}
            title="離開"
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwg208f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record ? [record.fg160_01] : []}
          keyOf={(item) => item.fg160_01}
          queryExact={queryAcceptanceExact}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => { if (record && !isEditing && record.fg160_80 === 'A') setEdit(); }}
          autoFillDisabled={!record || isEditing || record.fg160_80 !== 'A'}
          autoFillReason="請先載入一張草稿狀態的既有申請單；自動填表只會進入可修改草稿，不會送出。"
          screenDraft={{ master: formData, details, prepayments, invoice_items: invoiceItems }}
        />
      </div>
    </Spin>
  );
};

export default PWG208Form;
